import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  forwardRef,
  cloneElement,
  Children
} from 'react'
import PropTypes from 'prop-types'
import keyCode from 'keycode'
import {
  useEventHandler,
  useUpdateRef,
  useGlobalId,
  contains,
  Dropdown
} from '@zambezi/sdk/common'
import DropdownListTriggerWrapper from './DropdownListTriggerWrapper'

/**
 * Create a React Dropdown List Trigger component
 *
 * @param {import('./interfaces').DropdownListTriggerProps} props
 * @param {import('react').Ref} ref
 * @returns {JSX.Element}
 */
function dropdownListTrigger (
  {
    className,
    dropdownList,
    isDisabled,
    children,
    onShow,
    onHide,
    tabIndex: propsTabIndex,
    arrowOffset: propsArrowOffset,
    position: propsPosition
  },
  ref
) {
  const [position, setPosition] = useState(() =>
    normalizePosition(propsPosition)
  )
  const [isShown, setShown] = useState(false)
  const [aboutToShow, setAboutToShow] = useState(false)
  const dropdownId = useGlobalId('zb-dropdowntrigger-')
  /** @type {import('react').RefObject<HTMLElement>} */
  const elementRef = useRef()
  /** @type {import('react').RefObject<HTMLElement>} */
  const dropdownListRef = useRef()
  /** @type {import('react').RefObject<HTMLElement>} */
  const wrapperElementRef = useRef()
  const wrapperId = isDisabled ? null : `${dropdownId}-wrapper`
  /** @type {import('react').ReactElement} */
  const child = Children.only(children)
  const tabIndex = isDisabled ? -1 : propsTabIndex

  const show = useCallback(function show () {
    setAboutToShow(true)
  }, [])
  const hide = useCallback(function hide () {
    setAboutToShow(false)
  }, [])
  /** @type {(e: KeyboardEvent) => void} */
  const handleDocumentKeyDown = useCallback(function handleDocumentKeyDown (e) {
    setAboutToShow(isShown => (isShown ? keyCode(e) !== 'esc' : isShown))
  }, [])
  /** @type {(e: KeyboardEvent) => void} */
  const handleTriggerKeyDown = useCallback(
    function handleTriggerKeyDown (e) {
      if (isDisabled) return
      const key = keyCode(e)
      const shouldBePrevented =
        key === 'space' || key === 'down' || key === 'up'
      const shouldShow = (key === 'enter' || shouldBePrevented) && !isShown
      const shouldHide = key === 'esc' && isShown

      if (shouldBePrevented && !isShown) {
        e.preventDefault()
      }
      setAboutToShow(isShown => {
        if (!shouldHide) return shouldShow ? true : isShown
        return false
      })
      const { props: { onKeyDown } = {} } = child
      onKeyDown && typeof child.type !== 'function' && onKeyDown(e)
    },
    [isShown, isDisabled, child]
  )

  const [Trigger, triggerProps] = useMemo(() => {
    const props = {
      onKeyDown: handleTriggerKeyDown,
      ref: elementRef,
      tabIndex: wrapperId ? tabIndex : null,
      'aria-expanded': isShown
    }
    if (typeof child.type === 'function') {
      return [
        'span',
        {
          ...props,
          children: child
        }
      ]
    }
    return [
      child.type,
      {
        ...props,
        ...child.props,
        onKeyDown: handleTriggerKeyDown
      }
    ]
  }, [child, wrapperId, tabIndex, handleTriggerKeyDown])
  const Wrapper = useMemo(
    () =>
      forwardRef((props, ref) => (
        <DropdownListTriggerWrapper
          ref={ref}
          {...props}
          dropdownList={dropdownListRef}
        />
      )),
    []
  )
  const arrowOffset = useMemo(
    () => `calc(${propsArrowOffset} - ${-position.offset - 8}px)`,
    [position, propsArrowOffset]
  )
  const arrowPosition = useMemo(
    () => (position.align === 'left' ? 'left' : 'right'),
    [position]
  )

  useEffect(() => {
    if (isShown !== aboutToShow) {
      if (aboutToShow) {
        onShow()
      } else {
        if (
          wrapperElementRef.current &&
          contains(wrapperElementRef.current, document.activeElement)
        ) {
          elementRef.current &&
            elementRef.current.focus({ preventScroll: true })
        }
        onHide()
      }
      setShown(aboutToShow)
    }
  }, [isShown, aboutToShow, onShow, onHide])
  useEffect(() => setPosition(normalizePosition(propsPosition)), [
    propsPosition
  ])
  useEventHandler('keydown', handleDocumentKeyDown)
  useUpdateRef(ref, elementRef, { show, hide })

  return (
    <Dropdown
      className={className}
      wrapperId={wrapperId}
      Wrapper={Wrapper}
      Trigger={Trigger}
      triggerProps={triggerProps}
      showOn={isDisabled ? null : 'click'}
      isShown={isShown}
      onShow={show}
      onHide={hide}
      position={position}
      ref={wrapperElementRef}
      withHideOnClickOutside
      withHideOnClickIcon
      aria-haspopup='listbox'
      aria-disabled={isDisabled}
      role='button'
    >
      {dropdownList &&
        cloneElement(dropdownList, {
          id: dropdownId,
          onClose: hide,
          arrowPosition,
          arrowOffset,
          ref: dropdownListRef
        })}
    </Dropdown>
  )
}

/**
 * @param {import('./interfaces').DropdownListTriggerPosition} position
 * @returns {import('../common/interfaces').Position}
 */
function normalizePosition (position) {
  const isPositionString = typeof position === 'string'
  const offsetPosition = isNaN(position.offset)
    ? 0
    : -position.offset - 8
  return {
    direction: 'bottom',
    align: isPositionString ? position : position.align,
    offset: isPositionString
      ? 0
      : offsetPosition
  }
}

function noop () {}

const DropdownListTrigger = forwardRef(dropdownListTrigger)

DropdownListTrigger.displayName = 'DropdownListTrigger'

DropdownListTrigger.propTypes = {
  isDisabled: PropTypes.bool,
  className: PropTypes.string,
  dropdownList: PropTypes.node,
  position: PropTypes.oneOfType([
    PropTypes.shape({
      align: PropTypes.oneOf(['left', 'right']),
      offset: PropTypes.number
    }),
    PropTypes.string
  ]),
  arrowOffset: PropTypes.string,
  onHide: PropTypes.func,
  onShow: PropTypes.func,
  tabIndex: PropTypes.number
}

DropdownListTrigger.defaultProps = {
  isDisabled: false,
  dropdownList: null,
  position: { align: 'right', offset: -8 },
  arrowOffset: '0px',
  onHide: noop,
  onShow: noop,
  tabIndex: 0
}

export default DropdownListTrigger
